import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ReportGenerateModal.css";

const DELIVERY_UI_ENABLED = process.env.REACT_APP_REPORT_DELIVERY_UI_ENABLED === "1";
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function readableDate(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? "—" : parsed.toLocaleDateString();
}

function tidyError(error) {
  const message = String(error?.message || error || "Request failed");
  if (/\b503\b/.test(message)) return "Report delivery is not yet enabled for this organization.";
  if (/\b403\b/.test(message)) return "You are not authorized to perform this action.";
  if (/\b401\b/.test(message)) return "Your session needs to be refreshed.";
  return "The report operation could not be completed. Please try again or contact support.";
}

/** Customer-visible report library. Existing report rows are authoritative; no mock data. */
export default function AuditReportsPackages({ reports = [], loading = false, onRefresh, apiBaseUrl, getAccessToken, tenantId, userId }) {
  const [formOpen, setFormOpen] = useState(false);
  const modalRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const [selectedId, setSelectedId] = useState("");
  const [framework, setFramework] = useState("");
  const [frameworkOptions, setFrameworkOptions] = useState([]);
  const [frameworkLoading, setFrameworkLoading] = useState(false);
  const [frameworkError, setFrameworkError] = useState("");
  const [title, setTitle] = useState("Evidence Inventory Report");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!formOpen) return undefined;
    previouslyFocusedRef.current = document.activeElement;
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    modalRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !busy) setFormOpen(false);
      if (event.key !== "Tab") return;
      const items = modalRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]');
      if (!items?.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = priorOverflow;
      previouslyFocusedRef.current?.focus?.();
    };
  }, [formOpen, busy]);

  const counts = useMemo(() => ({
    available: reports.length,
    awaitingApproval: reports.filter((r) => r.status === "generated").length,
  }), [reports]);

  useEffect(() => {
    if (!formOpen || !DELIVERY_UI_ENABLED) return undefined;
    let cancelled = false;
    setFrameworkLoading(true);
    setFrameworkError("");
    setFrameworkOptions([]);
    setFramework("");
    (async () => {
      try {
        const token = await getAccessToken();
        if (!token) throw new Error("Authentication required");
        const response = await fetch(`${apiBaseUrl}/evidence/report-packs/frameworks`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
            ...(userId ? { "X-User-Id": userId } : {}),
          },
          cache: "no-store",
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data.frameworks)) throw new Error("Invalid framework authority response");
        const options = data.frameworks.filter((item) =>
          item && typeof item.key === "string" && item.key.trim() && typeof item.label === "string"
        );
        if (!cancelled) {
          setFrameworkOptions(options);
          setFramework(options.length === 1 ? options[0].key : "");
        }
      } catch (err) {
        if (!cancelled) setFrameworkError("Unable to load governed frameworks. Refresh or contact your OASYS administrator.");
      } finally {
        if (!cancelled) setFrameworkLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [formOpen, apiBaseUrl, getAccessToken, tenantId, userId]);

  const requestHeaders = async () => {
    const token = await getAccessToken();
    return {
      Authorization: `Bearer ${token}`,
      ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
      ...(userId ? { "X-User-Id": userId } : {}),
    };
  };

  const generate = async (event) => {
    event.preventDefault();
    if (!DELIVERY_UI_ENABLED || busy || frameworkLoading || frameworkError || !frameworkOptions.some((item) => item.key === framework)) return;
    setError(""); setNotice("");
    if (!DATE_PATTERN.test(periodStart) || !DATE_PATTERN.test(periodEnd) || periodEnd < periodStart) {
      setError("Select a valid reporting period."); return;
    }
    setBusy(true);
    try {
      const headers = await requestHeaders();
      const response = await fetch(`${apiBaseUrl}/evidence/report-packs/generate`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ framework, title: title.trim(), period_start: periodStart, period_end: periodEnd }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setNotice("Report generated. Review its version and integrity details before sharing.");
      setFormOpen(false);
      if (onRefresh) await onRefresh();
    } catch (e) { setError(tidyError(e)); }
    finally { setBusy(false); }
  };

  const download = async (record, fmt) => {
    if (!DELIVERY_UI_ENABLED || !record?.download_ready || downloading) return;
    const reportId = String(record.id || record.pack_id || "");
    if (!/^oasys-[a-f0-9]{32}$/.test(reportId)) { setError("Invalid report reference."); return; }
    setError("");
    setDownloading(`${reportId}:${fmt}`);
    try {
      const headers = await requestHeaders();
      const response = await fetch(`${apiBaseUrl}/evidence/report-packs/${encodeURIComponent(reportId)}/download?fmt=${fmt}`, {
        headers, cache: "no-store",
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      try {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `${reportId}.${fmt}`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } finally { window.setTimeout(() => URL.revokeObjectURL(url), 3000); }
    } catch (e) { setError(tidyError(e)); }
    finally { setDownloading(""); }
  };

  return (
    <section className="card" aria-label="Audit Reports and Packages" style={{ marginBottom: 20 }}>
      <div className="card-header" style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div className="card-title">Audit Reports &amp; Packages</div>
          <div className="muted" style={{ marginTop: 4 }}>Generate, review and securely download tenant-owned audit reports.</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button type="button" className="btn btn-sm" disabled={loading || busy} onClick={onRefresh}>Refresh reports</button>
          <button type="button" className="btn btn-sm" disabled={!DELIVERY_UI_ENABLED || busy} onClick={() => setFormOpen((v) => !v)} title={!DELIVERY_UI_ENABLED ? "Available after governed report delivery activation" : undefined}>Generate Report</button>
        </div>
      </div>
      <div className="card-body" style={{ display: "grid", gap: 16 }}>
        {!DELIVERY_UI_ENABLED ? <div className="muted" role="status">Report generation and downloads are not yet activated. Existing report records can be reviewed below.</div> : null}
        <div className="hero-kpis" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
          <div className="hero-kpi"><div className="kpi-label">REPORT VERSIONS</div><div className="kpi-value small">{counts.available}</div></div>
          <div className="hero-kpi"><div className="kpi-label">GENERATED · NOT APPROVED</div><div className="kpi-value small">{counts.awaitingApproval}</div></div>
          <div className="hero-kpi"><div className="kpi-label">AUDITOR ACCESS</div><div className="hero-kpi-foot">Not enabled in V1 rollout</div></div>
        </div>
        {error ? <div role="alert" style={{ color: "var(--danger)" }}>{error}</div> : null}
        {notice ? <div role="status" style={{ color: "var(--ok)" }}>{notice}</div> : null}
        {reports.length === 0 ? (
          <div className="muted" style={{ padding: "18px 0" }}>{loading ? "Loading reports…" : "No report versions have been returned for this organization."}</div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {reports.map((record, index) => {
              const id = String(record.id || record.pack_id || `report-${index}`);
              const downloadable = DELIVERY_UI_ENABLED && record.download_ready === true;
              return <div key={`${id}:${record.version || index}`} className="section-card" style={{ padding: 14, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                <div style={{ display: "grid", gap: 4 }}>
                  <button type="button" className="btn btn-ghost btn-sm" style={{ textAlign: "left" }} onClick={() => setSelectedId((current) => current === id ? "" : id)} aria-expanded={selectedId === id}>{record.title || "Report package"} · {selectedId === id ? "Hide details" : "View details"}</button>
                  <span className="muted">{record.framework || "Framework unspecified"} · Version {record.version || 1} · {record.status || "Unknown status"}</span>
                  <span className="muted">Created {readableDate(record.created_at)} · ZIP SHA-256: {record.sha256 ? `${String(record.sha256).slice(0, 16)}…` : "Not available"}</span>
                </div>
                {selectedId === id ? <div style={{ flexBasis: "100%", display: "grid", gap: 5 }}>
                  <span className="muted">Report reference: {id}</span>
                  <span className="muted">Framework: {record.framework || "Not specified"} · Created: {readableDate(record.created_at)}</span>
                  <span className="muted">Version: {record.version || 1} · State: {record.status || "Unknown"}</span>
                  <span className="muted">ZIP SHA-256: {record.sha256 || "Not recorded"}</span>
                  <span className="muted">Integrity status: {record.integrity_status || "Not independently verified"}</span>
                  {record.archive_id ? <span className="muted">Linked archive: {record.archive_id}</span> : null}
                  {record.linked_submission?.submission_id ? <span className="muted">Linked submission: {record.linked_submission.submission_id}</span> : null}
                </div> : null}
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn btn-sm" type="button" disabled={!downloadable || Boolean(downloading)} onClick={() => download(record, "pdf")}>PDF</button>
                  <button className="btn btn-sm" type="button" disabled={!downloadable || Boolean(downloading)} onClick={() => download(record, "zip")}>ZIP</button>
                </div>
              </div>;
            })}
          </div>
        )}
        <div className="muted" style={{ fontSize: 12 }}>Auditor invitations, approval, revocation and external sharing will appear only when their governed backend is implemented and verified. Generated reports are not third-party audit opinions.</div>
      </div>
      {formOpen && DELIVERY_UI_ENABLED && createPortal(
        <div className="oasys-report-modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) setFormOpen(false); }}>
          <div className="oasys-report-modal" role="dialog" aria-modal="true" aria-labelledby="oasys-report-title" tabIndex={-1} ref={modalRef}>
            <div className="oasys-report-modal-head">
              <div>
                <div className="oasys-report-eyebrow">Evidence &amp; Audit / Report Builder</div>
                <h2 id="oasys-report-title">Generate Audit Report</h2>
                <p className="oasys-report-subtitle">Create a private, versioned evidence inventory for this organization.</p>
              </div>
              <button type="button" className="oasys-report-iconbutton" aria-label="Close report dialog" disabled={busy} onClick={() => setFormOpen(false)}>×</button>
            </div>
            <form onSubmit={generate} className="oasys-report-modal-form">
              <div>
                <div className="oasys-report-section-label">Report details</div>
                <label className="oasys-report-field">
                  <span className="oasys-report-field-label">Framework</span>
                  <select required value={framework} disabled={frameworkLoading || Boolean(frameworkError) || frameworkOptions.length === 0} onChange={(e) => setFramework(e.target.value)}>
                    <option value="">{frameworkLoading ? "Loading applied frameworks…" : "Select an applied framework"}</option>
                    {frameworkOptions.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                  </select>
                </label>
                <p className="oasys-report-help">Options are governed by Framework Authority in the Team Console.</p>
                {frameworkError ? <p role="alert" className="oasys-report-modal-error">{frameworkError}</p> : null}
                {!frameworkLoading && !frameworkError && frameworkOptions.length === 0 ? <p role="status" className="oasys-report-help">No frameworks are applied. Contact your OASYS administrator.</p> : null}
              </div>
              <label className="oasys-report-field">
                <span className="oasys-report-field-label">Report title</span>
                <input required minLength={1} maxLength={255} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Security evidence inventory" />
              </label>
              <div>
                <div className="oasys-report-section-label">Reporting period</div>
                <div className="oasys-report-date-grid">
                  <label className="oasys-report-field">
                    <span className="oasys-report-field-label">Start date</span>
                    <input type="date" required value={periodStart} max={periodEnd || undefined} onChange={(e) => setPeriodStart(e.target.value)} />
                  </label>
                  <label className="oasys-report-field">
                    <span className="oasys-report-field-label">End date</span>
                    <input type="date" required value={periodEnd} min={periodStart || undefined} onChange={(e) => setPeriodEnd(e.target.value)} />
                  </label>
                </div>
              </div>
              <div className="oasys-report-scope-note">
                <strong>Evidence inventory — not an audit opinion</strong>
                This draft lists scoped evidence and its integrity metadata. Control effectiveness, compliance, and independent assurance are not automatically assessed.
              </div>
              {error ? <div className="oasys-report-modal-error" role="alert">{error}</div> : null}
              <div className="oasys-report-footer">
                <button type="button" className="oasys-report-secondary" disabled={busy} onClick={() => setFormOpen(false)}>Cancel</button>
                <button type="submit" className="oasys-report-primary" disabled={busy || frameworkLoading || Boolean(frameworkError) || !frameworkOptions.some((item) => item.key === framework) || !periodStart || !periodEnd || !title.trim() || periodEnd < periodStart}>{busy ? "Generating report…" : "Generate Draft Report"}</button>
              </div>
            </form>
          </div>
        </div>, document.body
      )}
    </section>
  );
}
